import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, Pencil, Plus, Trash2, X } from 'lucide-react';

import { ApiError, api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { RegionSelect } from './RegionSelect';
import type { AdminRegionSummary } from './types';

interface Holiday {
  id: string;
  date: string;
  name: string;
  isFullDay: boolean;
  regionCode: string;
  /** Municipio del festivo; '' = ámbito general de la región. */
  municipality: string;
}

interface ImportResult {
  year: number;
  regionCode: string;
  fetched: number;
  created: number;
  skipped: number;
}

const CURRENT_YEAR = new Date().getFullYear();
/** Sentinela para el filtro "Todas las provincias". */
const ALL_REGIONS = '__ALL__';
/** Sentinela para el filtro "Todos los ámbitos" (general + locales). */
const ALL_SCOPES = '__ALL__';
/** Sentinela para "Sólo festivos generales de la región" (municipality=''). */
const SCOPE_GENERAL = '__GENERAL__';

export function AdminHolidays() {
  const [year, setYear] = useState(CURRENT_YEAR);
  // Filtro de listado. `ALL_REGIONS` = mostrar todas las regiones en BD.
  const [filterRegion, setFilterRegion] = useState<string>(ALL_REGIONS);
  // Filtro adicional por municipio: ALL_SCOPES = todos; SCOPE_GENERAL = sólo
  // generales (no locales); cualquier otro string = municipio concreto.
  const [filterScope, setFilterScope] = useState<string>(ALL_SCOPES);
  // Región seleccionada para importar y para "Nuevo festivo" por defecto.
  const [importRegion, setImportRegion] = useState<string>('ES-AN-SE');
  const [editing, setEditing] = useState<Holiday | 'new' | null>(null);
  const [importMessage, setImportMessage] = useState<string | null>(null);

  const queryClient = useQueryClient();

  const regions = useQuery({
    queryKey: ['admin', 'regions'],
    queryFn: () => api<AdminRegionSummary[]>('/admin/holidays/regions'),
    staleTime: Infinity,
  });
  const regionByCode = useMemo(() => {
    const m = new Map<string, string>();
    for (const r of regions.data ?? []) m.set(r.code, r.name);
    return m;
  }, [regions.data]);

  const holidays = useQuery({
    queryKey: ['admin', 'holidays', year, filterRegion],
    queryFn: () => {
      const params = new URLSearchParams({ year: String(year) });
      if (filterRegion !== ALL_REGIONS) params.set('region', filterRegion);
      return api<Holiday[]>(`/admin/holidays?${params.toString()}`);
    },
  });

  const importMutation = useMutation({
    mutationFn: () =>
      api<ImportResult>('/admin/holidays/import', {
        method: 'POST',
        body: JSON.stringify({ year, regionCode: importRegion }),
      }),
    onSuccess: (r) => {
      const name = regionByCode.get(r.regionCode) ?? r.regionCode;
      setImportMessage(
        `Importación ${name} ${r.year}: ${r.created} creados, ${r.skipped} ya existían (${r.fetched} candidatos).`,
      );
      queryClient.invalidateQueries({ queryKey: ['admin', 'holidays', year] });
    },
  });

  const removeMutation = useMutation({
    mutationFn: (id: string) =>
      api<{ ok: true }>(`/admin/holidays/${id}`, { method: 'DELETE' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'holidays', year] }),
  });

  const allInYear = useMemo(() => holidays.data ?? [], [holidays.data]);

  // Lista única de municipios presentes en los festivos actualmente
  // cargados, para popular el selector de filtro. Se rehace al cambiar
  // año o región filtrada (que es lo que pide el backend).
  const municipalitiesInList = useMemo(() => {
    const set = new Set<string>();
    for (const h of allInYear) {
      if (h.municipality) set.add(h.municipality);
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'es'));
  }, [allInYear]);

  const list = useMemo(() => {
    if (filterScope === ALL_SCOPES) return allInYear;
    if (filterScope === SCOPE_GENERAL) return allInYear.filter((h) => h.municipality === '');
    return allInYear.filter((h) => h.municipality === filterScope);
  }, [allInYear, filterScope]);

  const yearOptions = useMemo(() => {
    const out: number[] = [];
    for (let y = CURRENT_YEAR - 1; y <= CURRENT_YEAR + 5; y++) out.push(y);
    return out;
  }, []);

  const selectCls =
    'h-9 w-full rounded-sm border border-input bg-card px-3 text-base text-foreground focus-visible:border-icaria-600 focus-visible:outline-none transition-colors';

  const importRegionName = regionByCode.get(importRegion) ?? importRegion;
  // Si el filtro es una provincia concreta, "Nuevo festivo" pre-rellena con ésa.
  // Si está en "Todas", usamos la región seleccionada para importar.
  const newHolidayRegion = filterRegion !== ALL_REGIONS ? filterRegion : importRegion;

  return (
    <div className="p-6 md:p-8">
      <div className="bg-card border border-border rounded-sm shadow-md overflow-hidden">
        <header className="px-7 py-5 border-b border-border flex flex-col gap-4">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <h2 className="text-lg font-semibold tracking-tightish">Calendario laboral</h2>
              <p className="text-[13px] text-muted-foreground mt-1">
                Festivos no laborables por provincia. Cada empleado sólo "ve" los de su región
                asignada en su ficha.
              </p>
            </div>
            <div className="inline-flex items-center gap-2 flex-wrap">
              <select
                value={year}
                onChange={(e) => setYear(Number(e.target.value))}
                className={cn(selectCls, 'w-auto min-w-[100px]')}
                aria-label="Año"
              >
                {yearOptions.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
              <Button variant="primary" size="sm" onClick={() => setEditing('new')}>
                <Plus className="h-3.5 w-3.5" />
                Nuevo festivo
              </Button>
            </div>
          </div>

          <div className="flex items-end gap-4 flex-wrap pt-2 border-t border-border">
            <div className="flex flex-col gap-1.5 min-w-[220px]">
              <Label htmlFor="hol-filter-region" className="text-[11px]">
                Filtrar por provincia
              </Label>
              <select
                id="hol-filter-region"
                value={filterRegion}
                onChange={(e) => setFilterRegion(e.target.value)}
                className={selectCls}
              >
                <option value={ALL_REGIONS}>Todas las provincias</option>
                {(regions.data ?? []).map((r) => (
                  <option key={r.code} value={r.code}>
                    {r.name} ({r.ca})
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1.5 min-w-[200px]">
              <Label htmlFor="hol-filter-scope" className="text-[11px]">
                Ámbito
              </Label>
              <select
                id="hol-filter-scope"
                value={filterScope}
                onChange={(e) => setFilterScope(e.target.value)}
                className={selectCls}
              >
                <option value={ALL_SCOPES}>Todos (generales + locales)</option>
                <option value={SCOPE_GENERAL}>Sólo generales de la provincia</option>
                {municipalitiesInList.map((m) => (
                  <option key={m} value={m}>
                    Local: {m}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1.5 min-w-[220px]">
              <Label htmlFor="hol-import-region" className="text-[11px]">
                Importar para
              </Label>
              <div className="flex gap-2">
                <RegionSelect
                  id="hol-import-region"
                  value={importRegion}
                  onChange={setImportRegion}
                />
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setImportMessage(null);
                if (
                  window.confirm(
                    `Importar festivos públicos de ${importRegionName} para ${year}?\n` +
                      'Se añaden nacionales + autonómicos. Los locales (de pueblo) se añaden a mano.\n' +
                      'Idempotente: los ya existentes para esta región y año se respetan.',
                  )
                ) {
                  importMutation.mutate();
                }
              }}
              disabled={importMutation.isPending || regions.isLoading}
              className="self-end"
            >
              <Download className="h-3.5 w-3.5" />
              {importMutation.isPending ? 'Importando…' : `Importar ${importRegionName}`}
            </Button>
          </div>
        </header>

        {importMessage ? (
          <div className="px-7 py-3 border-b border-border bg-success/5 flex items-center justify-between gap-3">
            <span className="text-[13px] text-success font-medium">{importMessage}</span>
            <button
              type="button"
              onClick={() => setImportMessage(null)}
              className="h-6 w-6 inline-flex items-center justify-center rounded-sm hover:bg-success/10 text-success"
              aria-label="Cerrar"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        ) : null}
        {importMutation.isError ? (
          <div className="px-7 py-3 border-b border-border bg-destructive/5">
            <p role="alert" className="text-[13px] text-destructive">
              {importMutation.error instanceof ApiError
                ? importMutation.error.message
                : 'Error al importar'}
            </p>
          </div>
        ) : null}

        {holidays.isLoading ? (
          <p className="px-7 py-10 text-center text-sm text-muted-foreground">Cargando…</p>
        ) : holidays.isError ? (
          <p role="alert" className="px-7 py-10 text-center text-sm text-destructive">
            {holidays.error instanceof ApiError ? holidays.error.message : 'Error al cargar festivos'}
          </p>
        ) : list.length === 0 ? (
          <p className="px-7 py-10 text-center text-sm text-muted-foreground">
            No hay festivos cargados para {year}
            {filterRegion !== ALL_REGIONS
              ? ` en ${regionByCode.get(filterRegion) ?? filterRegion}`
              : ''}
            . Pulsa "Importar {importRegionName}" o añade uno manualmente.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr>
                  <Th>Fecha</Th>
                  <Th>Nombre</Th>
                  <Th>Provincia</Th>
                  <Th>Ámbito</Th>
                  <Th align="right">{''}</Th>
                </tr>
              </thead>
              <tbody>
                {list.map((h) => (
                  <tr key={h.id} className="border-b border-border hover:bg-paper transition-colors">
                    <td className="px-5 py-4 font-mono tabular-nums">
                      <div className="text-[14px]">{h.date}</div>
                      <div className="text-[11px] text-muted-foreground capitalize">
                        {weekdayLabel(h.date)}
                      </div>
                    </td>
                    <td className="px-5 py-4 text-[14px]">{h.name}</td>
                    <td className="px-5 py-4 text-[12px] text-muted-foreground">
                      {regionByCode.get(h.regionCode) ?? h.regionCode}
                    </td>
                    <td className="px-5 py-4 text-[12px]">
                      {h.municipality ? (
                        <span className="inline-flex items-center gap-1 rounded-sm bg-amber-100 px-2 py-0.5 text-amber-900 font-medium">
                          Local · {h.municipality}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">General</span>
                      )}
                    </td>
                    <td className="px-5 py-4 text-right">
                      <div className="inline-flex gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setEditing(h)}
                          aria-label="Editar"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            if (window.confirm(`Borrar el festivo "${h.name}" del ${h.date}?`)) {
                              removeMutation.mutate(h.id);
                            }
                          }}
                          aria-label="Borrar"
                        >
                          <Trash2 className="h-3.5 w-3.5 text-destructive" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editing ? (
        <HolidayModal
          year={year}
          defaultRegionCode={newHolidayRegion}
          target={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            queryClient.invalidateQueries({ queryKey: ['admin', 'holidays', year] });
          }}
        />
      ) : null}
    </div>
  );
}

function Th({
  children,
  align = 'left',
}: {
  children: React.ReactNode;
  align?: 'left' | 'right';
}) {
  return (
    <th
      className={cn(
        'font-mono text-[12px] font-medium uppercase text-muted-foreground bg-paper border-b border-border px-5 py-3.5',
        align === 'right' ? 'text-right' : 'text-left',
      )}
      style={{ letterSpacing: '0.08em' }}
    >
      {children}
    </th>
  );
}

function HolidayModal({
  year,
  defaultRegionCode,
  target,
  onClose,
  onSaved,
}: {
  year: number;
  defaultRegionCode: string;
  target: Holiday | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isNew = target === null;
  const [date, setDate] = useState(target?.date ?? `${year}-01-01`);
  const [name, setName] = useState(target?.name ?? '');
  const [regionCode, setRegionCode] = useState(target?.regionCode ?? defaultRegionCode);
  const [municipality, setMunicipality] = useState(target?.municipality ?? '');

  const mutation = useMutation({
    mutationFn: () =>
      isNew
        ? api<Holiday>('/admin/holidays', {
            method: 'POST',
            body: JSON.stringify({ date, name, regionCode, municipality }),
          })
        : api<Holiday>(`/admin/holidays/${target!.id}`, {
            method: 'PATCH',
            body: JSON.stringify({ name, municipality }),
          }),
    onSuccess: onSaved,
  });

  const error = mutation.error
    ? mutation.error instanceof ApiError
      ? mutation.error.message
      : 'Error al guardar'
    : null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
    >
      <div
        aria-hidden
        onClick={onClose}
        className="absolute inset-0"
        style={{ background: 'rgba(10,14,31,0.4)', backdropFilter: 'blur(2px)' }}
      />
      <div className="relative w-full max-w-[440px] bg-card rounded-sm shadow-lg overflow-hidden">
        <header className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h2 className="text-[15px] font-semibold tracking-tightish">
            {isNew ? 'Nuevo festivo' : 'Editar festivo'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Cerrar"
            className="h-8 w-8 inline-flex items-center justify-center rounded-sm border border-line-strong bg-card text-foreground hover:border-foreground transition-colors"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </header>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!name.trim()) return;
            mutation.mutate();
          }}
        >
          <div className="px-6 py-5 flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="hol-date">
                Fecha <span className="text-destructive">*</span>
              </Label>
              <Input
                id="hol-date"
                type="date"
                required
                disabled={!isNew}
                value={date}
                onChange={(e) => setDate(e.target.value)}
              />
              {!isNew ? (
                <span className="text-[11px] text-muted-foreground">
                  Para cambiar la fecha o región, borra y crea de nuevo.
                </span>
              ) : null}
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="hol-name">
                Nombre <span className="text-destructive">*</span>
              </Label>
              <Input
                id="hol-name"
                type="text"
                required
                maxLength={200}
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ej. Día de Andalucía"
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="hol-region">
                Provincia <span className="text-destructive">*</span>
              </Label>
              <RegionSelect
                id="hol-region"
                value={regionCode}
                onChange={setRegionCode}
                disabled={!isNew}
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="hol-municipality">Municipio (opcional)</Label>
              <Input
                id="hol-municipality"
                type="text"
                maxLength={120}
                value={municipality}
                onChange={(e) => setMunicipality(e.target.value)}
                placeholder="Ej. Lebrija"
              />
              <span className="text-[11px] text-muted-foreground">
                Vacío = festivo general de la provincia (aplica a todos los empleados de la
                región). Si lo rellenas, sólo aplica a empleados cuyo municipio coincida
                exactamente.
              </span>
            </div>

            {error ? (
              <p role="alert" className="text-sm text-destructive">
                {error}
              </p>
            ) : null}
          </div>

          <footer className="flex justify-end gap-2 px-6 py-3.5 border-t border-border bg-paper">
            <Button type="button" variant="ghost" size="sm" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit" variant="primary" size="sm" disabled={mutation.isPending}>
              {mutation.isPending ? 'Guardando…' : 'Guardar'}
            </Button>
          </footer>
        </form>
      </div>
    </div>
  );
}

function weekdayLabel(ymd: string): string {
  const d = new Date(`${ymd}T00:00:00.000Z`);
  return d.toLocaleDateString('es-ES', { weekday: 'long', timeZone: 'UTC' });
}
